import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

// Formas que devuelve /api/v1/zoom/* (ver operativa_api/routes/zoom_routes.py).

export interface ZoomEstado {
  sincronizacion: "encendida" | "apagada";
  ultima_vuelta: { en?: string; ok?: boolean; nuevas?: number; error?: string };
  llamadas: number;
  primera: string | null;
  ultima: string | null;
}

export interface ZoomCola {
  cola_ext: string;
  cola: string;
  entradas: number;
  contestadas: number;
  abandonadas: number;
  abandono_pct: number;
  espera_prom_s: number;
  espera_max_s: number;
  semaforo: "verde" | "amarillo" | "rojo";
}

export interface ZoomColas {
  desde: string;
  hasta: string;
  colas: ZoomCola[];
  salientes: { total: number; contestadas: number };
}

export interface ZoomDemanda {
  desde: string;
  hasta: string;
  llamadas: number[][];
  abandonos: number[][];
}

export interface ZoomPosicion {
  ext: string;
  nombre: string;
  licencia: string;
  contestadas: number;
  salientes: number;
  sin_contestar: number;
  minutos: number;
  contesta_pct: number | null;
  sin_uso: boolean;
}

export interface ZoomLlamada {
  id: string;
  direccion: "entrante" | "saliente";
  inicio: string;
  duracion_s: number;
  resultado: string;
  numero_externo: string;
  cola_ext: string;
  cola: string;
  espera_s: number;
  agente_ext: string;
  agente: string;
  sin_contestar: string[];
  con_grabacion: boolean;
}

export interface ZoomLlamadas {
  total: number;
  pagina: number;
  por_pagina: number;
  items: ZoomLlamada[];
}

export interface Rango {
  desde: string;
  hasta: string;
}

const qs = (p: Record<string, string | number | undefined>) =>
  new URLSearchParams(
    Object.entries(p)
      .filter(([, v]) => v !== undefined && v !== "")
      .map(([k, v]) => [k, String(v)]),
  ).toString();

export function useZoomEstado() {
  return useQuery({
    queryKey: ["zoom", "estado"],
    queryFn: ({ signal }) => api<ZoomEstado>("/api/v1/zoom/estado", { signal }),
    refetchInterval: 60_000,
  });
}

export function useZoomColas(r: Rango) {
  return useQuery({
    queryKey: ["zoom", "colas", r],
    queryFn: ({ signal }) =>
      api<ZoomColas>(`/api/v1/zoom/colas?${qs({ ...r })}`, { signal }),
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
}

export interface FiltroDemanda {
  colas: string[];
  exts: string[];
}

export function useZoomDemanda(r: Rango, f: FiltroDemanda) {
  return useQuery({
    queryKey: ["zoom", "demanda", r, f],
    queryFn: ({ signal }) =>
      api<ZoomDemanda>(
        `/api/v1/zoom/demanda?${qs({ ...r, colas: f.colas.join(","), exts: f.exts.join(",") })}`,
        { signal },
      ),
    placeholderData: (prev) => prev,
  });
}

export interface ZoomFiltros {
  colas: { ext: string; nombre: string }[];
  posiciones: { ext: string; nombre: string }[];
}

export function useZoomFiltros(r: Rango) {
  return useQuery({
    queryKey: ["zoom", "filtros", r],
    queryFn: ({ signal }) =>
      api<ZoomFiltros>(`/api/v1/zoom/filtros?${qs({ ...r })}`, { signal }),
    placeholderData: (prev) => prev,
  });
}

export function useZoomPosiciones(r: Rango) {
  return useQuery({
    queryKey: ["zoom", "posiciones", r],
    queryFn: ({ signal }) =>
      api<{ posiciones: ZoomPosicion[] }>(
        `/api/v1/zoom/posiciones?${qs({ ...r })}`,
        { signal },
      ),
    placeholderData: (prev) => prev,
  });
}

export interface FiltroLlamadas extends Rango {
  numero?: string;
  cola?: string;
  agente?: string;
  resultado?: string;
  pagina?: number;
}

export function useZoomLlamadas(f: FiltroLlamadas) {
  return useQuery({
    queryKey: ["zoom", "llamadas", f],
    queryFn: ({ signal }) =>
      api<ZoomLlamadas>(`/api/v1/zoom/llamadas?${qs({ ...f })}`, { signal }),
    placeholderData: (prev) => prev,
  });
}

export const grabacionUrl = (id: string) =>
  `/api/v1/zoom/llamadas/${encodeURIComponent(id)}/grabacion`;

// ── Fotografía de la configuración (GET /api/v1/zoom/config) ──────────────

export type Obj = Record<string, unknown>;

export interface ZoomCambio {
  ruta: string;
  antes: unknown;
  despues: unknown;
}

export interface ZoomFotoResumen {
  id: number;
  tomada_en: string;
  tomada_por: string;
  cambios: number;
  errores: number;
}

export interface ZoomConfig {
  foto: {
    id: number;
    tomada_en: string;
    tomada_por: string;
    // Forma libre: la arma services/zoom_config.py (colas, recepcionistas,
    // numeros, reglas_ruteo, planes, usuarios). La pantalla lee lo que conoce.
    contenido: Record<string, Record<string, Obj>>;
    errores: string[];
    cambios: ZoomCambio[];
  } | null;
  historial: ZoomFotoResumen[];
}

export function useZoomConfig(fotoId?: number) {
  return useQuery({
    queryKey: ["zoom", "config", fotoId],
    queryFn: ({ signal }) =>
      api<ZoomConfig>(
        `/api/v1/zoom/config${fotoId ? `?foto_id=${fotoId}` : ""}`,
        { signal },
      ),
    placeholderData: (prev) => prev,
  });
}

export function useTomarFotoZoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ id: number; cambios: number; errores: string[] }>(
        "/api/v1/zoom/config/foto",
        {
          method: "POST",
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["zoom", "config"] }),
  });
}

// ---- Plan deseado vs Zoom (Fase B) ----

export interface ZoomPlanAccion {
  accion: string;
  objetivo: string;
  detalle: string;
  parametros: Obj;
  bloqueo: string | null;
  propuesta: boolean;
  firma: string;
  aprobacion_pendiente: string | null;
}

export interface ZoomPlanEntrada {
  clave: string;
  tipo: "cola" | "recepcionista";
  nombre: string;
  estado: "confirmado" | "pendiente_confirmar";
  en_zoom: string | null;
  asterisk: string | null;
  acciones: ZoomPlanAccion[];
  avisos: { tipo: string; texto: string }[];
}

export interface ZoomPlanCambio {
  id: number;
  approval_id: string | null;
  accion: string;
  objetivo: string;
  estado: string;
  detalle: string;
  creado_en: string;
}

export interface ZoomPlan {
  plan: {
    huella: string;
    actualizado: string | null;
    fuente: string | null;
    borrador: boolean;
  };
  foto: { id: number; tomada_en: string } | null;
  escritura: "encendida" | "apagada";
  borrador: boolean;
  resumen: {
    acciones: number;
    proponibles: number;
    bloqueadas: number;
    esperando_portabilidad: number;
    falta_usuario: number;
  };
  entradas: ZoomPlanEntrada[];
  cambios: ZoomPlanCambio[];
}

export function useZoomPlan() {
  return useQuery({
    queryKey: ["zoom", "plan"],
    queryFn: ({ signal }) => api<ZoomPlan>("/api/v1/zoom/plan", { signal }),
  });
}

export function useProponerPlanZoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ creadas: number; ya_pendientes: number; motivo?: string }>(
        "/api/v1/zoom/plan/proponer",
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zoom", "plan"] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });
}
